export type Rectangle = {
    left: number;
    right: number;
    top: number;
    bottom: number;
    width: number;
    height: number;
};

export const userFeatureHoldMilliseconds = 15_000;
export const gridZoomResetMilliseconds = 60_000;
export const featuredPhotoCaptionFadeMilliseconds = 400;
const wheelGridZoomSensitivity = 0.002;

export type ViewportTransform = {
    scale: number;
    panX: number;
    panY: number;
};

export type ImageBounds = {
    left: number;
    top: number;
    width: number;
    height: number;
};

export function calculateFeaturedImageFrameTransform(
    imageBounds: ImageBounds,
    frameBounds: Rectangle
): ViewportTransform {
    requireFinite('imageBounds.left', imageBounds.left);
    requireFinite('imageBounds.top', imageBounds.top);
    requirePositiveFinite('imageBounds.width', imageBounds.width);
    requirePositiveFinite('imageBounds.height', imageBounds.height);
    requireFinite('frameBounds.left', frameBounds.left);
    requireFinite('frameBounds.top', frameBounds.top);
    requirePositiveFinite('frameBounds.width', frameBounds.width);
    requirePositiveFinite('frameBounds.height', frameBounds.height);

    const scale = Math.min(
        frameBounds.width / imageBounds.width,
        frameBounds.height / imageBounds.height);
    return {
        scale,
        panX: frameBounds.left + (frameBounds.width - imageBounds.width * scale) * 0.5 - imageBounds.left * scale,
        panY: frameBounds.top + (frameBounds.height - imageBounds.height * scale) * 0.5 - imageBounds.top * scale
    };
}

function requirePositiveFinite(name: string, value: number): void {
    if (!Number.isFinite(value) || value <= 0) throw new RangeError(`${name} must be a positive finite number.`);
}

function requireFinite(name: string, value: number): void {
    if (!Number.isFinite(value)) throw new RangeError(`${name} must be finite.`);
}

function clamp(value: number, minimum: number, maximum: number): number {
    return Math.min(Math.max(value, minimum), maximum);
}

export function calculateFeaturedImageFitScale(
    imageWidth: number,
    imageHeight: number,
    viewportWidth: number,
    viewportHeight: number
): number {
    requirePositiveFinite('imageWidth', imageWidth);
    requirePositiveFinite('imageHeight', imageHeight);
    requirePositiveFinite('viewportWidth', viewportWidth);
    requirePositiveFinite('viewportHeight', viewportHeight);
    return Math.min(viewportWidth / imageWidth, viewportHeight / imageHeight);
}

function constrainImageAxis(scale: number, pan: number, viewportSize: number, imageStart: number, imageSize: number): number {
    const scaledStart = imageStart * scale;
    const scaledSize = imageSize * scale;
    if (scaledSize <= viewportSize) return (viewportSize - scaledSize) * 0.5 - scaledStart;
    const constrainedPan = clamp(pan, viewportSize - scaledStart - scaledSize, -scaledStart);
    return constrainedPan === 0 ? 0 : constrainedPan;
}

export function constrainViewportTransform(
    scale: number,
    panX: number,
    panY: number,
    viewportWidth: number,
    viewportHeight: number,
    imageBounds: ImageBounds = { left: 0, top: 0, width: viewportWidth, height: viewportHeight }
): ViewportTransform {
    requirePositiveFinite('scale', scale);
    requireFinite('panX', panX);
    requireFinite('panY', panY);
    requirePositiveFinite('viewportWidth', viewportWidth);
    requirePositiveFinite('viewportHeight', viewportHeight);
    requireFinite('imageBounds.left', imageBounds.left);
    requireFinite('imageBounds.top', imageBounds.top);
    requirePositiveFinite('imageBounds.width', imageBounds.width);
    requirePositiveFinite('imageBounds.height', imageBounds.height);

    return {
        scale,
        panX: constrainImageAxis(scale, panX, viewportWidth, imageBounds.left, imageBounds.width),
        panY: constrainImageAxis(scale, panY, viewportHeight, imageBounds.top, imageBounds.height)
    };
}

export function calculateFeaturedImagePinchTransform(options: {
    startingTransform: ViewportTransform;
    startingCenterX: number;
    startingCenterY: number;
    currentCenterX: number;
    currentCenterY: number;
    startingDistance: number;
    currentDistance: number;
    minimumScale: number;
    maximumScale: number;
    viewportWidth: number;
    viewportHeight: number;
    imageBounds?: ImageBounds;
}): ViewportTransform {
    requirePositiveFinite('startingTransform.scale', options.startingTransform.scale);
    requirePositiveFinite('startingDistance', options.startingDistance);
    requirePositiveFinite('currentDistance', options.currentDistance);
    requirePositiveFinite('minimumScale', options.minimumScale);
    requirePositiveFinite('maximumScale', options.maximumScale);
    if (options.minimumScale > options.maximumScale) throw new RangeError('Minimum scale cannot exceed maximum scale.');
    for (const [name, value] of [
        ['startingCenterX', options.startingCenterX],
        ['startingCenterY', options.startingCenterY],
        ['currentCenterX', options.currentCenterX],
        ['currentCenterY', options.currentCenterY]
    ] as const) requireFinite(name, value);

    const scale = clamp(
        options.startingTransform.scale * options.currentDistance / options.startingDistance,
        options.minimumScale,
        options.maximumScale);
    const contentX = (options.startingCenterX - options.startingTransform.panX) / options.startingTransform.scale;
    const contentY = (options.startingCenterY - options.startingTransform.panY) / options.startingTransform.scale;
    return constrainViewportTransform(
        scale,
        options.currentCenterX - contentX * scale,
        options.currentCenterY - contentY * scale,
        options.viewportWidth,
        options.viewportHeight,
        options.imageBounds);
}

export function calculateFeaturedImageWheelTransform(options: {
    startingTransform: ViewportTransform;
    focalPointX: number;
    focalPointY: number;
    scaleFactor: number;
    minimumScale: number;
    maximumScale: number;
    viewportWidth: number;
    viewportHeight: number;
    imageBounds?: ImageBounds;
}): ViewportTransform {
    return calculateFeaturedImagePinchTransform({
        startingTransform: options.startingTransform,
        startingCenterX: options.focalPointX,
        startingCenterY: options.focalPointY,
        currentCenterX: options.focalPointX,
        currentCenterY: options.focalPointY,
        startingDistance: 1,
        currentDistance: options.scaleFactor,
        minimumScale: options.minimumScale,
        maximumScale: options.maximumScale,
        viewportWidth: options.viewportWidth,
        viewportHeight: options.viewportHeight,
        imageBounds: options.imageBounds
    });
}

export function calculateFeaturedImagePanTransform(options: {
    startingTransform: ViewportTransform;
    startingX: number;
    startingY: number;
    currentX: number;
    currentY: number;
    viewportWidth: number;
    viewportHeight: number;
    imageBounds?: ImageBounds;
}): ViewportTransform {
    for (const [name, value] of [
        ['startingX', options.startingX],
        ['startingY', options.startingY],
        ['currentX', options.currentX],
        ['currentY', options.currentY]
    ] as const) requireFinite(name, value);

    return constrainViewportTransform(
        options.startingTransform.scale,
        options.startingTransform.panX + options.currentX - options.startingX,
        options.startingTransform.panY + options.currentY - options.startingY,
        options.viewportWidth,
        options.viewportHeight,
        options.imageBounds);
}

export function calculatePinchItemsPerRow(
    startingItemsPerRow: number,
    startingDistance: number,
    currentDistance: number,
    minimumItemsPerRow: number,
    maximumItemsPerRow: number
): number {
    if (![startingItemsPerRow, startingDistance, currentDistance, minimumItemsPerRow, maximumItemsPerRow]
        .every(value => Number.isFinite(value) && value > 0)) {
        throw new RangeError('Pinch density values must be positive finite numbers.');
    }
    if (minimumItemsPerRow > maximumItemsPerRow) throw new RangeError('Minimum items per row cannot exceed maximum items per row.');

    const itemsPerRow = startingItemsPerRow * startingDistance / currentDistance;
    return Math.min(Math.max(itemsPerRow, minimumItemsPerRow), maximumItemsPerRow);
}

export function calculateWheelItemsPerRow(
    startingItemsPerRow: number,
    deltaY: number,
    minimumItemsPerRow: number,
    maximumItemsPerRow: number
): number {
    if (![startingItemsPerRow, minimumItemsPerRow, maximumItemsPerRow]
        .every(value => Number.isFinite(value) && value > 0)
        || !Number.isFinite(deltaY)) {
        throw new RangeError('Wheel density values must contain positive densities and a finite delta.');
    }
    if (minimumItemsPerRow > maximumItemsPerRow) throw new RangeError('Minimum items per row cannot exceed maximum items per row.');

    const itemsPerRow = startingItemsPerRow * Math.exp(deltaY * wheelGridZoomSensitivity);
    return Math.min(Math.max(itemsPerRow, minimumItemsPerRow), maximumItemsPerRow);
}

export function decelerateVelocity(velocity: number, deceleration: number, elapsedSeconds: number): number {
    if (!Number.isFinite(velocity) || !Number.isFinite(deceleration) || !Number.isFinite(elapsedSeconds)
        || deceleration <= 0 || elapsedSeconds <= 0) return velocity;

    const nextSpeed = Math.max(0, Math.abs(velocity) - deceleration * elapsedSeconds);
    return Math.sign(velocity) * nextSpeed;
}

/** Moves a velocity toward a target without overshooting it. */
export function approachVelocity(
    velocity: number,
    targetVelocity: number,
    acceleration: number,
    elapsedSeconds: number
): number {
    if (![velocity, targetVelocity, acceleration, elapsedSeconds].every(Number.isFinite)
        || acceleration <= 0 || elapsedSeconds <= 0) return velocity;

    const maximumChange = acceleration * elapsedSeconds;
    if (velocity < targetVelocity) return Math.min(velocity + maximumChange, targetVelocity);
    if (velocity > targetVelocity) return Math.max(velocity - maximumChange, targetVelocity);
    return velocity;
}

/** Calculates the distance required to reach zero under constant deceleration. */
export function calculateStoppingDistance(velocity: number, deceleration: number): number {
    if (!Number.isFinite(velocity) || !Number.isFinite(deceleration) || deceleration <= 0) return 0;
    return velocity * velocity / (2 * deceleration);
}

/** Projects travel while velocity approaches a target at constant acceleration. */
export function calculateVelocityTransitionDistance(
    velocity: number,
    targetVelocity: number,
    acceleration: number,
    elapsedSeconds: number
): number {
    if (![velocity, targetVelocity, acceleration, elapsedSeconds].every(Number.isFinite)
        || acceleration <= 0 || elapsedSeconds <= 0) return 0;

    const transitionSeconds = Math.min(Math.abs(targetVelocity - velocity) / acceleration, elapsedSeconds);
    const endingVelocity = approachVelocity(velocity, targetVelocity, acceleration, transitionSeconds);
    return (velocity + endingVelocity) * 0.5 * transitionSeconds
        + targetVelocity * (elapsedSeconds - transitionSeconds);
}

/** Converts browser wheel delta units into vertical CSS pixels. */
export function normalizeWheelDelta(deltaY: number, deltaMode: number, viewportHeight: number): number {
    if (!Number.isFinite(deltaY) || !Number.isFinite(deltaMode)) return 0;
    if (deltaMode === 1) return deltaY * 16;
    if (deltaMode === 2) return Number.isFinite(viewportHeight) && viewportHeight > 0
        ? deltaY * viewportHeight
        : 0;
    return deltaY;
}

/** Accumulates wheel input into a bounded grid scrolling velocity. */
export function calculateWheelMomentumVelocity(
    currentVelocity: number,
    wheelDelta: number,
    momentumPerPixel: number,
    maximumVelocity: number
): number {
    if (![currentVelocity, wheelDelta, momentumPerPixel, maximumVelocity].every(Number.isFinite)
        || momentumPerPixel <= 0 || maximumVelocity <= 0) return currentVelocity;

    return Math.max(
        -maximumVelocity,
        Math.min(maximumVelocity, currentVelocity + wheelDelta * momentumPerPixel));
}

export function isRectangleVisible(rectangle: Rectangle, viewportWidth: number, viewportHeight: number): boolean {
    return rectangle.width > 0 && rectangle.height > 0
        && rectangle.right > 0 && rectangle.left < viewportWidth
        && rectangle.bottom > 0 && rectangle.top < viewportHeight;
}

export function isRectangleFullyVisibleVertically(rectangle: Rectangle, viewportHeight: number): boolean {
    return rectangle.height > 0 && rectangle.top >= 0 && rectangle.bottom <= viewportHeight;
}

export function selectFeatureCandidateRowIndexes(
    rowRectangles: readonly Rectangle[],
    viewportHeight: number
): number[] {
    const fullyVisibleIndexes = rowRectangles
        .map((rectangle, index) => isRectangleFullyVisibleVertically(rectangle, viewportHeight) ? index : -1)
        .filter(index => index >= 0);
    if (fullyVisibleIndexes.length > 0) return fullyVisibleIndexes;

    let lowerVisibleRowIndex = -1;
    for (let index = 0; index < rowRectangles.length; index++) {
        const rectangle = rowRectangles[index];
        if (rectangle.height <= 0 || rectangle.bottom <= 0 || rectangle.top >= viewportHeight) continue;
        if (lowerVisibleRowIndex < 0 || rectangle.top > rowRectangles[lowerVisibleRowIndex].top) {
            lowerVisibleRowIndex = index;
        }
    }

    return lowerVisibleRowIndex < 0 ? [] : [lowerVisibleRowIndex];
}

/** Selects the best current grid rectangle for a featured photograph to return to. */
export function selectFeatureReturnRectangle(
    rectangles: readonly Rectangle[],
    viewportWidth: number,
    viewportHeight: number
): Rectangle | null {
    if (rectangles.length === 0) return null;

    const viewportCenterX = viewportWidth * 0.5;
    const viewportCenterY = viewportHeight * 0.5;
    return rectangles.reduce((best, candidate) => {
        const candidateVisible = isRectangleVisible(candidate, viewportWidth, viewportHeight);
        const bestVisible = isRectangleVisible(best, viewportWidth, viewportHeight);
        if (candidateVisible !== bestVisible) return candidateVisible ? candidate : best;

        const candidateDistance = Math.hypot(
            candidate.left + candidate.width * 0.5 - viewportCenterX,
            candidate.top + candidate.height * 0.5 - viewportCenterY);
        const bestDistance = Math.hypot(
            best.left + best.width * 0.5 - viewportCenterX,
            best.top + best.height * 0.5 - viewportCenterY);
        return candidateDistance < bestDistance ? candidate : best;
    });
}

export function selectFeatureCenteringAdjustment(
    rectangles: readonly Rectangle[],
    viewportWidth: number,
    viewportHeight: number,
    contentHeight: number
): { rectangleIndex: number; scrollAdjustment: number } | null {
    if (rectangles.length === 0 || contentHeight <= 0) return null;

    const viewportCenterX = viewportWidth * 0.5;
    const viewportCenterY = viewportHeight * 0.5;
    const normalizeAdjustment = (adjustment: number): number => {
        const wrapped = ((adjustment + contentHeight * 0.5) % contentHeight + contentHeight) % contentHeight;
        return wrapped - contentHeight * 0.5;
    };
    const selection = rectangles
        .map((rectangle, rectangleIndex) => {
            const scrollAdjustment = normalizeAdjustment(rectangle.top + rectangle.height * 0.5 - viewportCenterY);
            const horizontalDistance = rectangle.left + rectangle.width * 0.5 - viewportCenterX;
            return {
                rectangleIndex,
                scrollAdjustment,
                distance: Math.hypot(horizontalDistance, scrollAdjustment)
            };
        })
        .reduce((best, candidate) => candidate.distance < best.distance ? candidate : best);
    return {
        rectangleIndex: selection.rectangleIndex,
        scrollAdjustment: selection.scrollAdjustment
    };
}

export function selectFeatureCaptionPosition(
    poiX: number,
    poiY: number,
    viewportWidth: number,
    viewportHeight: number
): { horizontal: 'left' | 'right'; vertical: 'top' | 'bottom' } {
    if (![poiX, poiY, viewportWidth, viewportHeight].every(Number.isFinite)
        || viewportWidth <= 0
        || viewportHeight <= 0) {
        throw new RangeError('Caption positioning requires finite coordinates and positive viewport dimensions.');
    }

    return {
        horizontal: poiX <= viewportWidth * 0.5 ? 'right' : 'left',
        vertical: poiY <= viewportHeight * 0.5 ? 'bottom' : 'top'
    };
}

export function advanceLoopOffset(currentOffset: number, distance: number, contentHeight: number): number {
    if (!Number.isFinite(currentOffset) || !Number.isFinite(distance) || !Number.isFinite(contentHeight) || contentHeight <= 0) return 0;
    return ((currentOffset + distance) % contentHeight + contentHeight) % contentHeight;
}
